import { Link } from 'react-router-dom';

import type { BillingScheduleStatus } from '@solar/contracts';

import { Alert } from '../../../../components/Alert';
import { PageSpinner } from '../../../../components/PageSpinner';
import portalStyles from '../../../../components/PortalComponents.module.css';
import { StatusBadge } from '../../../../components/StatusBadge';
import { billingApi } from '../../../../features/billing/billing.api';
import { useSessionQuery as useQuery } from '../../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatBusinessDate } from '../../../../lib/format-date';
import { routeTo } from '../../../../routes/paths';
import styles from '../../AppPages.module.css';

function needsAuthorization(status: BillingScheduleStatus): boolean {
  return (
    status === 'DUE' ||
    status === 'AWAITING_CUSTOMER_AUTHORIZATION' ||
    status === 'OTP_SENT' ||
    status === 'OVERDUE'
  );
}

function authorizeHref(schedule: {
  id: string;
  activeAuthorizationSessionId: string | null;
}): string {
  if (schedule.activeAuthorizationSessionId !== null) {
    return routeTo.customerPaymentAuthorize(schedule.activeAuthorizationSessionId);
  }

  return routeTo.customerPaymentAuthorizeForSchedule(schedule.id);
}

export function RepaymentsPage(): React.JSX.Element {
  const repayments = useQuery({
    queryKey: ['customer', 'repayments'],
    queryFn: () => billingApi.listRepayments({ pageSize: 50 }),
  });

  const items = repayments.data?.items ?? [];
  const urgentItems = items.filter((schedule) => needsAuthorization(schedule.status));

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Repayments</h1>
          <p className={styles.pageSubtitle}>
            Upcoming and due instalments. Authorize payments here when required.
          </p>
        </div>
        <Link to={routeTo.customerPaymentHistory()}>Payment history</Link>
      </header>

      {urgentItems.length > 0 && (
        <section
          className={`${portalStyles.heroBanner} ${portalStyles.heroBannerUrgent}`}
          aria-labelledby="repayments-attention-heading"
        >
          <div className={portalStyles.heroBannerContent}>
            <p className={portalStyles.heroBannerEyebrow}>Action required</p>
            <h2 className={portalStyles.heroBannerTitle} id="repayments-attention-heading">
              {urgentItems.length === 1
                ? 'One instalment needs authorization'
                : `${urgentItems.length} instalments need authorization`}
            </h2>
            <p className={portalStyles.heroBannerDescription}>
              {formatNgn(urgentItems[0]?.instalmentAmount)} due{' '}
              {formatBusinessDate(urgentItems[0]?.dueDate ?? null)}
            </p>
          </div>
          {urgentItems[0] !== undefined && (
            <Link className={portalStyles.heroBannerAction} to={authorizeHref(urgentItems[0])}>
              Authorize payment
            </Link>
          )}
        </section>
      )}

      <section className={styles.card} aria-labelledby="repayments-heading">
        <h2 className={styles.cardTitle} id="repayments-heading">
          Your instalments
        </h2>

        {repayments.isPending && <PageSpinner label="Loading repayments" />}
        {repayments.isError && <Alert tone="error">{toDisplayMessage(repayments.error)}</Alert>}

        {repayments.isSuccess && items.length === 0 && (
          <p className={styles.empty}>No instalments yet.</p>
        )}

        {repayments.isSuccess && items.length > 0 && (
          <div className={portalStyles.compactTableWrapper}>
            <table className={portalStyles.compactTable} aria-label="Repayments">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Due date</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Paid</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>{schedule.sequenceNumber}</td>
                    <td>{formatBusinessDate(schedule.dueDate)}</td>
                    <td>{formatNgn(schedule.instalmentAmount)}</td>
                    <td>{formatNgn(schedule.amountPaid)}</td>
                    <td>
                      <StatusBadge status={schedule.status} />
                    </td>
                    <td>
                      {needsAuthorization(schedule.status) ? (
                        <Link to={authorizeHref(schedule)}>Authorize payment</Link>
                      ) : schedule.status === 'PAID' ? (
                        <Link to={routeTo.customerPaymentHistory()}>View payments</Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
